import { create } from 'zustand';

import { type DetectTextResult, type SubmitTranslationsResult } from '@air/feature-ag-ui-chat';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getNodeDimensions } from '../utils/nodeDimensions';
import { getEditCanvasBridge } from './editCanvasBridge';
import { analyzeLocalizationElements } from './localizationElementAnalysis';
import { buildDetectedElementsFromOcr } from './localizationElementDetection';
import { getLocalizationTextDirection } from './localizationLanguages';
import { normalizeBoundingBox } from './localizationProtectedRegions';
import { recordRecentLocalizationLanguage } from './localizationRecentLanguages';
import { buildTranslationSpanId, parseTranslationSpanId } from './localizationTranslationSpanId';
import {
  type LocalizationBoundingBox,
  type LocalizationElement,
  type LocalizationImageDimensions,
  type LocalizationProtectedRegion,
  type LocalizationProtectedRegionGeometry,
  type LocalizationTextDirection,
} from './localizationTypes';

export type LocalizationStep = 'language' | 'review';

/** OCR lifecycle: kicked off on open, resolves when `detect_text` returns. */
export type LocalizationSplitStatus = 'idle' | 'running' | 'ready' | 'failed';

/** `'cancelled'` is not a phase the run itself can be in — it's what a suppressed, otherwise-idle run reads as. */
export type LocalizationTranslateStatus = 'idle' | 'running' | 'ready' | 'failed' | 'cancelled';

/**
 * A running run carries its own request: which spans it asked for, and the language/direction it
 * asked in. A "running" phase with no request attached would be a state nothing can act on
 * correctly — every consumer of an in-flight run needs to know what it is a run *of*.
 */
export type LocalizationTranslateRun =
  | { status: 'idle' }
  | { status: 'failed' }
  | { status: 'ready' }
  | {
      spanIds: string[];
      status: 'running';
      targetLanguage: string;
      textDirection: LocalizationTextDirection;
      /** Identifies this specific run, so a settle that lands after a newer run started can recognize it no longer owns the phase. */
      token: number;
    };

/** How a translate run's underlying send resolved, for `beginTranslateRun`'s handle to settle against. */
export type LocalizationTranslateSendOutcome =
  /**
   * The agent rejected the send outright because the session's lock was still held by a prior run, so its spans never dispatched.
   * Settles to `'failed'` rather than auto-resending, since the lock can outlive the client's own "done" and retrying against it
   * would turn one bounce into a hot loop — the user's own Try again is the only way back in.
   */
  | 'busy'
  /** The send resolved (or rejected for any other reason) without a `submit_translations` result ever landing. */
  | 'unresolved';

export type LocalizationTranslateRunHandle = {
  /** No-ops once a later `beginTranslateRun` has replaced this run — see `settleTranslateRun`. */
  settle: (outcome: LocalizationTranslateSendOutcome) => void;
};

/**
 * Distinct from the session's own `sourceNodeId` so a retry after `'failed'` can re-claim without resetting the session.
 * `dispatched` is the single source of truth for whether a send has gone out for this claim, so the watching effect reads the
 * store fresh and a retry's fresh `dispatched: false` re-arms it without needing a remount.
 */
export type LocalizationOcrRequest = { dispatched: boolean; sourceNodeId: string } | null;

/** The Protected-list entry currently hovered — read only by the canvas overlay, never by a review row (see `selectActiveLocalizationElementId`/`selectActiveLocalizationSpanId`). */
export type LocalizationHoveredProtectedEntry = { elementId: string; wordIndex: number | null };

type LocalizationState = {
  active: boolean;
  /** Span ids ever sent to the model in this session, keyed for O(1) lookup. Reset by `open()` and by committing a new target language — the same boundary that clears `elements[].translations`. */
  attemptedSpanIds: string[];
  elements: LocalizationElement[];
  focusedElementId: string | null;
  /** Translation span the user is acting on, so a split line pairs one fragment instead of the whole line. */
  focusedSpanId: string | null;
  hoveredElementId: string | null;
  hoveredProtectedEntry: LocalizationHoveredProtectedEntry | null;
  hoveredSpanId: string | null;
  imageDimensions: LocalizationImageDimensions | null;
  isMarkingProtectedRegions: boolean;
  /**
   * Whether the agent run fetching translations is still open. Distinct from the run's own phase,
   * which settles the moment a `submit_translations` result lands — the underlying send keeps going
   * for a beat after that, and Generate can't send until it ends.
   */
  isTranslateRunActive: boolean;
  /** Latched by entering marking, cleared by leaving it (or by committing a language selection) — see `setIsMarkingProtectedRegions` and `advanceStep`. Independent of the run's own phase, which is why no ref watching a marking transition is needed to recover it. */
  isTranslateSuppressed: boolean;
  /** The token of the most recently begun run, kept past that run settling so a late-arriving send outcome can still recognize itself as the latest and clear `isTranslateRunActive`. */
  latestTranslateRunToken: number | null;
  ocrRequest: LocalizationOcrRequest;
  protectedRegions: LocalizationProtectedRegion[];
  selectedLanguage: string;
  sourceFileName: string;
  sourceNodeId: string | null;
  splitStatus: LocalizationSplitStatus;
  step: LocalizationStep;
  targetLanguage: string;
  textDirection: LocalizationTextDirection;
  translateRun: LocalizationTranslateRun;
};

type LocalizationActions = {
  /** The only way any text becomes excluded: drawing an area by hand and pressing Skip both land here. */
  addProtectedRegion: (
    boundingBox: LocalizationBoundingBox,
    createdBy: LocalizationProtectedRegion['createdBy'],
  ) => void;
  advanceStep: () => void;
  applyOcrResult: (sourceNodeId: string, result: DetectTextResult) => void;
  applyTranslationResult: (targetLanguage: string, result: SubmitTranslationsResult) => void;
  backStep: () => void;
  /** Starts (or restarts, after a `'failed'` claim) the OCR request for `sourceNodeId`. */
  beginOcrRequest: (sourceNodeId: string) => void;
  /** The only way to open a translate run; returns a handle whose `settle` is the only way to close it. */
  beginTranslateRun: (
    spanIds: string[],
    targetLanguage: string,
    textDirection: LocalizationTextDirection,
  ) => LocalizationTranslateRunHandle;
  /** Suppresses further auto-translate and drops a running/idle run back to idle. No-op once the run has already settled — there is nothing left to cancel. */
  cancelTranslateRun: () => void;
  close: () => void;
  failOcrRequest: (sourceNodeId: string) => void;
  /** Settles directly to `'ready'` with no request ever dispatched — for when a run could begin but there is nothing left to translate. */
  markNothingPendingToTranslate: () => void;
  /** Flips the current claim's `dispatched` bit once its send has actually gone out. No-op against a stale or superseded claim, so a send that starts before a retry re-opens it can't mark the new claim already spent. */
  markOcrDispatched: (sourceNodeId: string) => void;
  open: (sourceNodeId: string) => void;
  /** The only undo any exclusion has. Takes a list because freeing one word means clearing every area that covers it, not just the first. */
  removeProtectedRegions: (regionIds: string[]) => void;
  /** No-op when nothing was ever fetched for this span — there is nothing to go back to. */
  resetElementTranslation: (elementId: string, spanId: string) => void;
  /** The only way back from `'failed'` into an eligible phase — the review step's Try again calls this. No-op from any other phase, so a stray click can't clobber a run that's already active or settled some other way. */
  retryTranslateRun: () => void;
  setElementTranslation: (elementId: string, spanId: string, translatedText: string) => void;
  setFocusedElementId: (elementId: string | null) => void;
  setFocusedSpanId: (spanId: string | null) => void;
  setHoveredElementId: (elementId: string | null) => void;
  setHoveredProtectedEntry: (entry: LocalizationHoveredProtectedEntry | null) => void;
  setHoveredSpanId: (spanId: string | null) => void;
  setIsMarkingProtectedRegions: (isMarkingProtectedRegions: boolean) => void;
  setSelectedLanguage: (language: string) => void;
};

const INITIAL_STATE: LocalizationState = {
  active: false,
  attemptedSpanIds: [],
  elements: [],
  focusedElementId: null,
  focusedSpanId: null,
  hoveredElementId: null,
  hoveredProtectedEntry: null,
  hoveredSpanId: null,
  imageDimensions: null,
  isMarkingProtectedRegions: false,
  isTranslateRunActive: false,
  isTranslateSuppressed: false,
  latestTranslateRunToken: null,
  ocrRequest: null,
  protectedRegions: [],
  selectedLanguage: '',
  sourceFileName: '',
  sourceNodeId: null,
  splitStatus: 'idle',
  step: 'language',
  targetLanguage: '',
  textDirection: 'ltr',
  translateRun: { status: 'idle' },
};

/** Monotonic and module-scoped, never reset by `close()` — a counter restarted at 0 per session would let a stale run's late settle collide with id 1 of the next session and read as belonging to it. */
let nextTranslateRunToken = 1;

/** One node lookup for both the file name shown on the card and the dimensions a region can be drawn against before OCR reports its own. */
const getSourceNodeInfo = (
  sourceNodeId: string,
): { fileName: string; imageDimensions: LocalizationImageDimensions | null } => {
  const sourceNode = getEditCanvasBridge()
    .getNodes()
    .find((node) => node.id === sourceNodeId);

  if (sourceNode?.type !== 'image') {
    return { fileName: 'Image', imageDimensions: null };
  }

  const { height, width } = getNodeDimensions(sourceNode);

  return {
    fileName: sourceNode.data.fileName ?? 'Image',
    imageDimensions: width > 0 && height > 0 ? { height, width } : null,
  };
};

/**
 * Needed both when an area is added (which can exclude the focused element or retire its span) and when one is removed (merging
 * segments can retire the span id a focused fragment was keyed to, too). Resolved against the coverage the resulting regions
 * produce, rather than against which call changed the list, so every caller clears focus by the same rule.
 */
const reconcileFocusForProtectedRegions = (
  state: Pick<LocalizationState, 'elements' | 'focusedElementId' | 'focusedSpanId' | 'imageDimensions'>,
  protectedRegions: LocalizationProtectedRegionGeometry[],
): Pick<LocalizationState, 'focusedElementId' | 'focusedSpanId'> => {
  if (!state.focusedElementId && !state.focusedSpanId) {
    return { focusedElementId: state.focusedElementId, focusedSpanId: state.focusedSpanId };
  }

  const analyses = analyzeLocalizationElements(state.elements, protectedRegions, state.imageDimensions);
  const focusedAnalysis = analyses.find((analysis) => analysis.element.id === state.focusedElementId);
  const focusedSpanElementId = state.focusedSpanId ? parseTranslationSpanId(state.focusedSpanId)?.elementId : null;
  const spanAnalysis =
    focusedSpanElementId === state.focusedElementId
      ? focusedAnalysis
      : analyses.find((analysis) => analysis.element.id === focusedSpanElementId);
  const isFocusedSpanStillOffered =
    state.focusedSpanId !== null && (spanAnalysis?.segments.some(({ id }) => id === state.focusedSpanId) ?? false);

  return {
    focusedElementId: focusedAnalysis && !focusedAnalysis.isTranslatable ? null : state.focusedElementId,
    focusedSpanId: isFocusedSpanStillOffered ? state.focusedSpanId : null,
  };
};

/**
 * Exported as a pure reducer, testable directly against every (status × outcome) pair without a handle or `renderHook`. Guards on
 * token and status so a stale or already-superseded settle leaves the phase untouched; a busy bounce additionally un-marks its
 * spans in `attemptedSpanIds` so a manual retry can resend them instead of finding them already "attempted".
 */
export const settleTranslateRun = (
  run: LocalizationTranslateRun,
  token: number,
  outcome: LocalizationTranslateSendOutcome,
  attemptedSpanIds: string[],
): { attemptedSpanIds: string[]; translateRun: LocalizationTranslateRun } => {
  if (run.status !== 'running' || run.token !== token) {
    return { attemptedSpanIds, translateRun: run };
  }

  if (outcome === 'busy') {
    const bouncedSpanIds = new Set(run.spanIds);

    return {
      attemptedSpanIds: attemptedSpanIds.filter((spanId) => !bouncedSpanIds.has(spanId)),
      translateRun: { status: 'failed' },
    };
  }

  return { attemptedSpanIds, translateRun: { status: 'failed' } };
};

/** Whether a new translate run may begin: OCR is ready, the user has reached review, nothing has suppressed auto-translate, and no run already owns the phase. */
export const canBeginTranslateRun = (
  state: Pick<LocalizationState, 'isTranslateSuppressed' | 'splitStatus' | 'step' | 'translateRun'>,
): boolean =>
  state.step === 'review' &&
  state.splitStatus === 'ready' &&
  !state.isTranslateSuppressed &&
  (state.translateRun.status === 'idle' || state.translateRun.status === 'ready');

/**
 * Only aborts when translate's own send is what's occupying the thread — `translateStatus === 'running'` already answers that,
 * since it reads `'running'` only once translate's own send has begun, not while an unrelated run (e.g. OCR's) is in flight.
 */
export const shouldAbortAgentRun = (
  translateStatus: LocalizationTranslateStatus,
  currentThreadId: string | null,
): boolean => translateStatus === 'running' && currentThreadId !== null;

export const useLocalizationStore = create<LocalizationState & LocalizationActions>((set, get) => ({
  ...INITIAL_STATE,

  /**
   * Only the area list changes: which elements an area excludes is derived at read time by
   * `analyzeLocalizationElements`, so exclusion is never written onto an element and deleting the
   * area is always enough to restore it.
   */
  addProtectedRegion: (boundingBox, createdBy) =>
    set((state) => {
      if (!state.imageDimensions) {
        // Unreachable from the UI — marking is gated until OCR sets these dimensions. Reported rather than dropped, so a
        // vanishing region doesn't silently read to the user as "my protected area didn't save".
        reportErrorToBugsnag({
          context: 'Dropped a protected region drawn before image dimensions were known',
          error: new Error('addProtectedRegion called with no imageDimensions'),
        });

        return state;
      }

      const protectedRegions: LocalizationProtectedRegion[] = [
        ...state.protectedRegions,
        {
          boundingBox: normalizeBoundingBox(boundingBox, state.imageDimensions),
          createdBy,
          id: crypto.randomUUID(),
        },
      ];

      return { ...reconcileFocusForProtectedRegions(state, protectedRegions), protectedRegions };
    }),

  advanceStep: () => {
    const { selectedLanguage, step, targetLanguage } = get();

    if (step !== 'language') {
      return;
    }

    const trimmed = selectedLanguage.trim();

    if (!trimmed) {
      return;
    }

    // A different target language invalidates every translation fetched (or typed) for the old
    // one — restart the fetch lifecycle so the review list re-fetches. Re-committing the same
    // language leaves an already-fetched or edited review alone.
    const isNewTargetLanguage = trimmed !== targetLanguage;

    set((state) => ({
      isMarkingProtectedRegions: false,
      // A fresh language commit supersedes a stale cancel from before Back, so re-selecting the same language after a cancel
      // doesn't leave the session suppressed with no path back to translating.
      isTranslateSuppressed: false,
      step: 'review',
      targetLanguage: trimmed,
      textDirection: getLocalizationTextDirection(trimmed),
      ...(isNewTargetLanguage
        ? {
            attemptedSpanIds: [],
            elements: state.elements.map((element) => ({
              ...element,
              fetchedTranslations: {},
              translationOrigins: {},
              translations: {},
            })),
            isTranslateRunActive: false,
            translateRun: { status: 'idle' as const },
          }
        : {}),
    }));
  },

  /**
   * The result of `applyOcrResult` landing is trusted only when it answers the claim currently
   * outstanding: a payload for a different image, or one that arrives after the session moved on,
   * would otherwise rebuild the review list from scratch and discard every translation and skip the
   * user already set.
   */
  applyOcrResult: (sourceNodeId, result) => {
    const session = get();

    if (!session.active || session.ocrRequest?.sourceNodeId !== sourceNodeId || session.splitStatus !== 'running') {
      return;
    }

    const { elements, imageDimensions } = buildDetectedElementsFromOcr(result);

    set({ elements, imageDimensions, splitStatus: 'ready' });
  },

  /**
   * Guards only on `targetLanguage` — deliberately looser than `settleTranslateRun`'s token guard, since a stale result's span
   * ids simply fail to match anything requested and are dropped entry by entry. Each entry's composite id
   * (`elementId:startWordIndex:textDigest`) routes it back to its span; an id that doesn't parse, no longer resolves, or names a
   * span already marked `'user'` origin is skipped rather than failing the whole batch.
   */
  applyTranslationResult: (targetLanguage, result) => {
    const session = get();

    if (
      !session.active ||
      session.translateRun.status !== 'running' ||
      session.translateRun.targetLanguage !== targetLanguage
    ) {
      return;
    }

    const requestedSpanIds = new Set(session.translateRun.spanIds);
    const translationsByElementId = new Map<string, Map<string, string>>();

    for (const entry of result.translations) {
      const parsed = parseTranslationSpanId(entry.id);

      if (!parsed) {
        continue;
      }

      // When the model mangles the digest in an echoed id, rebuilding it from the `originalText` it echoed (required to be the
      // source string) recovers the span it meant. An entry that still matches nothing is dropped rather than stored under a key
      // nothing reads, which is what made a mistyped id look like a blank field.
      const spanId = requestedSpanIds.has(entry.id)
        ? entry.id
        : buildTranslationSpanId(parsed.elementId, parsed.startWordIndex, entry.originalText);

      if (!requestedSpanIds.has(spanId)) {
        continue;
      }

      const spanTranslations = translationsByElementId.get(parsed.elementId) ?? new Map<string, string>();

      spanTranslations.set(spanId, entry.translatedText);
      translationsByElementId.set(parsed.elementId, spanTranslations);
    }

    const elements = session.elements.map((element) => {
      const spanTranslations = translationsByElementId.get(element.id);

      if (!spanTranslations) {
        return element;
      }

      const fetchedTranslations = { ...element.fetchedTranslations };
      const translationOrigins = { ...element.translationOrigins };
      const translations = { ...element.translations };

      for (const [spanId, translatedText] of spanTranslations) {
        // Recorded even when the span is user-edited and left alone below — Reset means "what the
        // model says now", not whatever it said on an earlier fetch before the segmentation changed.
        fetchedTranslations[spanId] = translatedText;

        if (translationOrigins[spanId] === 'user') {
          continue;
        }

        translationOrigins[spanId] = 'fetched';
        translations[spanId] = translatedText;
      }

      return { ...element, fetchedTranslations, translationOrigins, translations };
    });

    // A requested span missing from the result (and not user-authored) fails the batch, judged against what was requested
    // rather than the segmentation now — marking can reshape segments mid-flight without that being a failure. One exception: a
    // line since protected outright has nothing visible missing, so a dropped id for it doesn't fail the batch.
    const fullyProtectedElementIds = new Set(
      analyzeLocalizationElements(session.elements, session.protectedRegions, session.imageDimensions)
        .filter((analysis) => !analysis.isTranslatable)
        .map((analysis) => analysis.element.id),
    );
    const hasMissingSpan = session.translateRun.spanIds.some((spanId) => {
      const parsed = parseTranslationSpanId(spanId);

      if (!parsed || fullyProtectedElementIds.has(parsed.elementId)) {
        return false;
      }

      const element = session.elements.find(({ id }) => id === parsed.elementId);

      if (!element || element.translationOrigins[spanId] === 'user') {
        return false;
      }

      return !translationsByElementId.get(parsed.elementId)?.has(spanId);
    });

    set({ elements, translateRun: { status: hasMissingSpan ? 'failed' : 'ready' } });
  },

  backStep: () => {
    if (get().step === 'review') {
      set({ isMarkingProtectedRegions: false, step: 'language' });
    }
  },

  beginOcrRequest: (sourceNodeId) => set({ ocrRequest: { dispatched: false, sourceNodeId }, splitStatus: 'running' }),

  beginTranslateRun: (spanIds, targetLanguage, textDirection) => {
    const token = nextTranslateRunToken++;

    set((state) => ({
      attemptedSpanIds: Array.from(new Set([...state.attemptedSpanIds, ...spanIds])),
      isTranslateRunActive: true,
      latestTranslateRunToken: token,
      translateRun: { spanIds, status: 'running', targetLanguage, textDirection, token },
    }));

    return {
      settle: (outcome) => {
        set((state) => {
          const phaseUpdate = settleTranslateRun(state.translateRun, token, outcome, state.attemptedSpanIds);

          return {
            ...phaseUpdate,
            // Looser than the phase guard above on purpose: `applyTranslationResult` typically settles
            // the phase before the underlying send resolves, but the send itself is still open until
            // it does, and Generate must not act while it's the *latest* run doing so — even one whose
            // phase has already moved on.
            isTranslateRunActive: state.latestTranslateRunToken === token ? false : state.isTranslateRunActive,
          };
        });
      },
    };
  },

  /**
   * Meaningful only while a run could still be waited on — `'idle'` counts too, since that's the phase for the whole time OCR is
   * still splitting, and cancelling still needs to record the intent not to auto-translate once it lands. No-op from
   * `'ready'`/`'failed'`, which have already delivered an outcome.
   */
  cancelTranslateRun: () => {
    const { translateRun } = get();

    if (translateRun.status !== 'running' && translateRun.status !== 'idle') {
      return;
    }

    set({ isTranslateSuppressed: true, translateRun: { status: 'idle' } });
  },

  close: () => set({ ...INITIAL_STATE }),

  /** Same id-and-phase guard as `applyOcrResult`/`markOcrDispatched`: a rejection for an abandoned claim, or one arriving after the claim already reached `'ready'`, must not touch the current status. */
  failOcrRequest: (sourceNodeId) =>
    set((state) =>
      state.ocrRequest?.sourceNodeId === sourceNodeId && state.splitStatus === 'running'
        ? { splitStatus: 'failed' }
        : {},
    ),

  /** Only from `'idle'`: an already-settled run has already produced its own outcome and must not be overwritten. */
  markNothingPendingToTranslate: () =>
    set((state) => (state.translateRun.status === 'idle' ? { translateRun: { status: 'ready' } } : {})),

  markOcrDispatched: (sourceNodeId) =>
    set((state) =>
      state.ocrRequest?.sourceNodeId === sourceNodeId ? { ocrRequest: { dispatched: true, sourceNodeId } } : {},
    ),

  open: (sourceNodeId) => {
    const { fileName, imageDimensions } = getSourceNodeInfo(sourceNodeId);

    set({
      ...INITIAL_STATE,
      active: true,
      imageDimensions,
      ocrRequest: { dispatched: false, sourceNodeId },
      sourceFileName: fileName,
      sourceNodeId,
      splitStatus: 'running',
    });
  },

  /** Deleting an area restores the rows it covered, since exclusion was never written onto them. */
  removeProtectedRegions: (regionIds) =>
    set((state) => {
      const protectedRegions = state.protectedRegions.filter((region) => !regionIds.includes(region.id));

      return { ...reconcileFocusForProtectedRegions(state, protectedRegions), protectedRegions };
    }),

  resetElementTranslation: (elementId, spanId) =>
    set((state) => ({
      elements: state.elements.map((element) => {
        if (element.id !== elementId) {
          return element;
        }

        const fetchedText = element.fetchedTranslations[spanId];

        if (fetchedText === undefined) {
          return element;
        }

        return {
          ...element,
          translationOrigins: { ...element.translationOrigins, [spanId]: 'fetched' },
          translations: { ...element.translations, [spanId]: fetchedText },
        };
      }),
    })),

  retryTranslateRun: () =>
    set((state) => (state.translateRun.status === 'failed' ? { translateRun: { status: 'idle' } } : {})),

  setElementTranslation: (elementId, spanId, translatedText) =>
    set((state) => ({
      elements: state.elements.map((element) =>
        element.id === elementId
          ? {
              ...element,
              translationOrigins: { ...element.translationOrigins, [spanId]: 'user' },
              translations: { ...element.translations, [spanId]: translatedText },
            }
          : element,
      ),
    })),

  setFocusedElementId: (focusedElementId) => set({ focusedElementId }),

  setFocusedSpanId: (focusedSpanId) => set({ focusedSpanId }),

  setHoveredElementId: (hoveredElementId) => set({ hoveredElementId }),

  setHoveredProtectedEntry: (hoveredProtectedEntry) => set({ hoveredProtectedEntry }),

  setHoveredSpanId: (hoveredSpanId) => set({ hoveredSpanId }),

  setIsMarkingProtectedRegions: (isMarkingProtectedRegions) =>
    set((state) => ({
      focusedElementId: isMarkingProtectedRegions ? null : state.focusedElementId,
      // Cleared alongside the element it names — `selectActiveLocalizationSpanId` falls back to this when nothing is hovered,
      // so a span outliving its element is the shape behind every stale-highlight bug this panel has had.
      focusedSpanId: isMarkingProtectedRegions ? null : state.focusedSpanId,
      hoveredElementId: null,
      hoveredProtectedEntry: null,
      hoveredSpanId: null,
      isMarkingProtectedRegions,
      // Entering marking always suppresses, regardless of whether a run is in flight, since the segmentation is about to change
      // either way. Deliberately doesn't touch `translateRun` itself — retiring a running run is `cancelTranslateRun`'s job,
      // called alongside this one by whoever orchestrates entering marking — so calling this setter alone never loses a result
      // its run is still waiting on.
      isTranslateSuppressed: isMarkingProtectedRegions,
    })),

  setSelectedLanguage: (language) => {
    const trimmed = language.trim();

    if (!trimmed) {
      return;
    }

    recordRecentLocalizationLanguage(trimmed);

    set({ selectedLanguage: trimmed });
  },
}));

/**
 * Derives status from the run's own phase plus the suppression latch rather than storing either as a mirror field — a
 * `set({ translateStatus })` call anywhere would reopen the two-sources-of-truth bug a materialized status field caused before.
 * `'cancelled'` is reported, never stored: it is exactly "suppressed with nothing running".
 */
export const selectTranslateStatus = (
  state: Pick<LocalizationState, 'isTranslateSuppressed' | 'translateRun'>,
): LocalizationTranslateStatus =>
  state.isTranslateSuppressed && state.translateRun.status === 'idle' ? 'cancelled' : state.translateRun.status;

export const selectIsTranslateRunActive = (state: Pick<LocalizationState, 'isTranslateRunActive'>): boolean =>
  state.isTranslateRunActive;

/**
 * The one region highlighted on both surfaces — the source-image overlays and the review list.
 * Nothing is highlighted until the user points at or picks a region: focus starts empty and is only
 * set by clicking a region or a translation input, so moving the pointer away no longer falls back
 * to an arbitrary region. Hover still wins over focus, which stays put while the user edits a row.
 */
export const selectActiveLocalizationElementId = ({
  focusedElementId,
  hoveredElementId,
}: {
  focusedElementId: string | null;
  hoveredElementId: string | null;
}): string | null => hoveredElementId ?? focusedElementId;

/**
 * Null while the user is on a whole line, so every box of that line pairs. Set only when they are
 * on one fragment of a split line, which narrows the pairing to that fragment's words.
 */
export const selectActiveLocalizationSpanId = ({
  focusedSpanId,
  hoveredSpanId,
}: {
  focusedSpanId: string | null;
  hoveredSpanId: string | null;
}): string | null => hoveredSpanId ?? focusedSpanId;

/**
 * Whether a translate pass has produced anything to look at yet — the readiness check that keeps
 * the review list on screen during marking rather than hiding it, the empty-field case that hiding
 * it was originally for.
 */
export const selectHasLocalizationTranslations = ({ elements }: Pick<LocalizationState, 'elements'>): boolean =>
  elements.some((element) => Object.values(element.translations).some((text) => text.trim().length > 0));
